import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import moment, { Moment } from 'moment'
import { isEmpty } from 'underscore'
import { ADVANCED_ANALYTICS, IPlotVariable, IBestDataset } from '../../../../types/analytics'
import { IAdvancedAnalyticsApiPayload } from '../../../../types/chart'
import { IEnrichedMetadata } from '../../../../types/metadata'
import { DT0Format } from '../../../../utils/StaticVariable'
import { IAnalytcisUserInput } from '../../../../types/IAnalyticsUserInput'
import { MarkerItem } from '../../../../types/home'
import { error } from 'console'

interface UseTidalLevelParams {
    coordinates: number[]
    previewDatasetId: string
    datasets: IBestDataset[]
    onDatasetChange: (item: string) => void
    dateRange: [moment.Moment, moment.Moment]
    enrichedMetadata: IEnrichedMetadata[]
    onUpdate: (payload: IAdvancedAnalyticsApiPayload) => void
    tidalLevelValueImage: string
    tidalValueLinks: string[]
    error: string
    updating: boolean
    pointName: string
    userInput: IAnalytcisUserInput
    updateUserInput: React.Dispatch<Partial<IAnalytcisUserInput>>
    point?: MarkerItem
    globalDateRange: any[]
    setGlobalDateRange: (item: any) => void
    isForAllAnalytics: boolean
    setIsForAllAnalytics: (item: any) => void
}

const supportedItems = ['WL', 'WL_Tide']

export const useTidalParameters = (params: UseTidalLevelParams) => {
    const {
      coordinates,
      previewDatasetId,
      datasets,
      onDatasetChange,
      dateRange,
      enrichedMetadata,
      onUpdate,
      tidalLevelValueImage,
      pointName,
      userInput,
      updateUserInput,
      globalDateRange,
      setGlobalDateRange,
      isForAllAnalytics,
      setIsForAllAnalytics,
      error
    } = params

    const selfTriggered = useRef(false)

    const [listVariablesX, setListVariableX] = useState<IPlotVariable[]>([])
    const [dataset, setDataset] = useState(userInput.TidalLevel.dataset)
    const [variableX, setVariableX] = useState<IPlotVariable | null>(userInput.TidalLevel.variable)
    const [startDate, setStartDate] = useState(userInput.TidalLevel.startDate || moment(dateRange[0]))
    const [endDate, setEndDate] = useState(userInput.TidalLevel.endDate || moment(dateRange[1]))
    const [supported, setSupported] = useState<boolean>(false)

    const getImage = useCallback((
        indexX: IPlotVariable,
        orderId: string,
        startDate: string,
        endDate: string
    ) => {
        const [longitude, latitude] = coordinates
        if (indexX) {
            const payload: IAdvancedAnalyticsApiPayload = {
                PointName: pointName,
                OrderId: orderId,
                OriginalOrderId: orderId,
                Longitude: longitude,
                Latitude: latitude,
                StartDate: startDate,
                EndDate: endDate,
                Title: indexX.Name,
                AcronymX: indexX.ShortName ? indexX.ShortName : indexX.Name,
                ColumnIndexX: indexX.Index,
                IntervalCountX: 16,
                AnalyticType: ADVANCED_ANALYTICS.TIDAL_LEVEL,
                DatasetId: previewDatasetId,
                OriginalDatasetId: previewDatasetId,
            }
            onUpdate(payload)
        }
    }, [coordinates, onUpdate, previewDatasetId, pointName])

    const onUpdateTidalLevel = useCallback(
        (dateRange?: any) => {
            if (enrichedMetadata && variableX) {
                const task = enrichedMetadata.find(
                    (task: IEnrichedMetadata) =>
                        task.DatasetId === dataset &&
                        task.CoordinatesString === coordinates.join()
                )

                if (task) {
                    getImage(
                        variableX,
                        task.OrderId,
                        dateRange ? dateRange[0].format(DT0Format) : startDate.format(DT0Format),
                        dateRange ? dateRange[1].format(DT0Format) : endDate.format(DT0Format)
                    )
                }
            }
        },
        [enrichedMetadata, dataset, startDate, endDate, variableX, coordinates, getImage]
    )

    const doVariableChange = useCallback((active: number) => {
        const item = listVariablesX.find(e => e.Index === active)
        setVariableX(item!)
    }, [listVariablesX])

    const doStartDateChange = useCallback((value: any) => {
        if (value) {
            setStartDate(value)
            if (isForAllAnalytics) {
                setGlobalDateRange([value, endDate])
            }
        }
    }, [endDate, isForAllAnalytics, setGlobalDateRange])

    const doEndDateChange = useCallback((value: any) => {
        if (value) {
            setEndDate(value)
            if (isForAllAnalytics) {
                setGlobalDateRange([startDate, value])
            }
        }
    }, [startDate, isForAllAnalytics, setGlobalDateRange])

    const onSelectDataset = useCallback((value: string) => {
        onDatasetChange(value)
    }, [onDatasetChange])

    const handleCheckboxChange = useCallback(() => {
        setGlobalDateRange([startDate, endDate])
        setIsForAllAnalytics(!isForAllAnalytics)
        selfTriggered.current = true
    }, [startDate, endDate, setGlobalDateRange, isForAllAnalytics, setIsForAllAnalytics])

    const disabledStartDate = useCallback((current: Moment) => {
        const isAfterEndDate = current && current > endDate
        return isAfterEndDate
            ? isAfterEndDate
            : current.isBefore(dateRange[0], 'day')
    }, [endDate, dateRange])

    const disabledEndDate = useCallback((current: Moment) => {
        const isBeforeStartDate = current && current < startDate
        return isBeforeStartDate
            ? isBeforeStartDate
            : current.isAfter(dateRange[1], 'day')
    }, [startDate, dateRange])

    const categories = useMemo(() => {
        const label = datasets.map((bd: IBestDataset) => bd.label)
        return Array.from(new Set(label))
    }, [datasets])

    useEffect(() => {
        updateUserInput({
            TidalLevel: {
                dataset: dataset,
                variable: variableX,
                startDate: startDate,
                endDate: endDate,
            },
        })
    }, [dataset, variableX, startDate, endDate, updateUserInput])

    useEffect(() => {
        if (isForAllAnalytics && tidalLevelValueImage !== '') {
            if (selfTriggered.current) {
                selfTriggered.current = false
                return
            }
            if (globalDateRange[0] !== startDate || globalDateRange[1] !== endDate) {
                onUpdateTidalLevel(globalDateRange)
                setStartDate(globalDateRange[0])
                setEndDate(globalDateRange[1])
            }
        }
    }, [isForAllAnalytics, tidalLevelValueImage, globalDateRange, startDate, endDate])

    useEffect(() => {
        if (enrichedMetadata) {
            const task = enrichedMetadata.find(
                (task: IEnrichedMetadata) =>
                    task.DatasetId === previewDatasetId &&
                    task.CoordinatesString === coordinates.join()
            )
            if (task) {
                const itemsInfos = task.ItemsInfo.filter(
                    (itemInfo: IPlotVariable) =>
                        itemInfo.ShortName && supportedItems.includes(itemInfo.ShortName)
                )
                setListVariableX(itemsInfos)

                if (previewDatasetId && !isEmpty(datasets) && !isEmpty(coordinates)) {
                    setDataset(previewDatasetId)
                    let dateRange = [startDate, endDate]
                    if (isForAllAnalytics) {
                        dateRange = globalDateRange
                        setStartDate(dateRange[0])
                        setEndDate(dateRange[1])
                    }

                    if (itemsInfos.length > 0) {
                        setSupported(true)
                        const indexX = itemsInfos[0]
                        setVariableX(indexX)
                        if (tidalLevelValueImage === '' && error === '') {
                            selfTriggered.current = true
                            getImage(
                                indexX,
                                task.OrderId,
                                dateRange[0].format(DT0Format),
                                dateRange[1].format(DT0Format)
                            )
                        }
                    } else {
                        setSupported(false)
                    }
                }
            }
        }
    }, [previewDatasetId, datasets, enrichedMetadata, coordinates, tidalLevelValueImage, isForAllAnalytics, globalDateRange, error])

    return {
        listVariablesX,
        dataset,
        variableX,
        startDate,
        endDate,
        supported,
        categories,
        onUpdateTidalLevel,
        doVariableChange,
        doStartDateChange,
        doEndDateChange,
        onSelectDataset,
        handleCheckboxChange,
        disabledStartDate,
        disabledEndDate,
    }
}
